import * as React from 'react';

import type { ImportEventTrigger } from '@gitbook/app-analytics';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Alert,
    Button,
    Card,
    CardActions,
    CardBody,
    CardImage,
    CardImageLayout,
    CardWrapperButton,
    type CardWrapperButtonProps,
    Description,
    Dialog,
    DialogBody,
    DialogHeader,
    DialogRoot,
    DialogTrigger,
    Heading,
    ProgressiveImage,
    Separator,
    useDialogClose,
} from '@gitbook/spine-react';

import { importFile, importWebsite, siteMultipleClients } from '../../assets';
import { useSetAnalyticsScreen } from '../../navigation';
import { useCurrentOrganization } from '../../state';
import { ImportFilesDialog } from './ImportFilesDialog';
import { ImportWebsiteDialog } from './ImportWebsiteDialog';
import { type ImportResolvedTarget, type ImportTarget, ImportTargetContext } from './TargetContext';

export interface ImportContentDialogProps {
    /**
     * What triggered the import (for analytics purposes).
     */
    trigger: ImportEventTrigger;
    /**
     * The target where to import the content.
     */
    target: ImportTarget;
    /**
     * Callback when the import starts.
     */
    onStart?: (target: ImportResolvedTarget) => void;
    /**
     * Default source.
     */
    defaultSource?: 'file' | 'website';
}

/**
 * Modal to import content into a target.
 */
export function ImportContentDialog(props: {
    /**
     * What triggered the import (for analytics purposes).
     */
    trigger: ImportEventTrigger;
    /**
     * The target where to import the content.
     */
    target: ImportTarget;
    /**
     * Callback when the import starts.
     */
    onStart?: (target: ImportResolvedTarget) => void | Promise<void>;
    /**
     * Default source.
     */
    defaultSource?: 'file' | 'website';
}) {
    return (
        <Dialog size={{ width: 664 }}>
            <ImportContentDialogContent {...props} />
        </Dialog>
    );
}

function ImportContentDialogContent(props: ImportContentDialogProps) {
    const { trigger, target, defaultSource, onStart } = props;
    const closeDialog = useDialogClose();
    const handleStart = async (target: ImportResolvedTarget) => {
        await onStart?.(target);
        closeDialog();
    };
    return (
        <>
            <DialogHeader>
                <Heading>Import</Heading>
                <Description>Import from different sources straight into GitBook.</Description>
            </DialogHeader>
            <ImportTargetContext target={target}>
                {({ resolveTarget }) => {
                    if (!resolveTarget) {
                        return (
                            <DialogBody>
                                <Alert
                                    kind="warning"
                                    title="You do not have permission to import here."
                                />
                            </DialogBody>
                        );
                    }

                    return (
                        <DialogBody className="flex flex-col gap-6">
                            <ImportAnalyticsScreen trigger={trigger} />
                            <div className="flex gap-6">
                                <DialogRoot defaultOpen={defaultSource === 'file'}>
                                    <DialogTrigger>
                                        <ImportCardButton
                                            image={{ src: importFile }}
                                            title="File"
                                            description="Import from Markdown, HTML or ZIP files."
                                        />
                                    </DialogTrigger>
                                    <ImportFilesDialog
                                        resolveTarget={resolveTarget}
                                        onStart={handleStart}
                                        trigger={trigger}
                                    />
                                </DialogRoot>
                                <DialogRoot defaultOpen={defaultSource === 'website'}>
                                    <DialogTrigger>
                                        <ImportCardButton
                                            image={{
                                                src: importWebsite,
                                            }}
                                            title="Online docs"
                                            description="Import from any public documentation."
                                        />
                                    </DialogTrigger>
                                    <ImportWebsiteDialog
                                        resolveTarget={resolveTarget}
                                        onStart={handleStart}
                                        trigger={trigger}
                                    />
                                </DialogRoot>
                            </div>

                            <Separator />

                            <NeedHelpCard />
                        </DialogBody>
                    );
                }}
            </ImportTargetContext>
        </>
    );
}

/**
 * Track the import screen in analytics.
 */
function ImportAnalyticsScreen(props: { trigger: ImportEventTrigger }) {
    const { trigger } = props;
    useSetAnalyticsScreen({ screen: 'import_start', trigger }, [trigger]);
    return null;
}

const ImportCardButton = React.forwardRef(function ImportCardButton(
    props: { title: string; description: string } & Omit<CardWrapperButtonProps, 'children'>,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const { title, description, className, ...rest } = props;
    return (
        <CardWrapperButton
            ref={ref}
            className="flex-1"
            {...rest}
            image={{ ...rest.image, className: 'aspect-[300/128]' }}
        >
            <div>
                <Heading level={4}>{title}</Heading>
                <Description>{description}</Description>
            </div>
        </CardWrapperButton>
    );
});

function NeedHelpCard() {
    const organization = useCurrentOrganization();
    return (
        <Card>
            <CardImageLayout>
                <CardBody>
                    <Heading>Need help migrating your docs? </Heading>
                    <Description>
                        Our white-glove migration service ensures your content moves smoothly into
                        GitBook.
                    </Description>
                    <CardActions>
                        <Button
                            kind="secondary"
                            label="Talk to our team"
                            target="_blank"
                            href="https://www.gitbook.com/enterprise"
                            onPress={() => {
                                analytics.logEvent(
                                    analytics.createOrgEvent(organization, {
                                        type: 'organization_open_enterprise_cta',
                                    })
                                );
                            }}
                        />
                    </CardActions>
                </CardBody>
                <CardImage>
                    <ProgressiveImage src={siteMultipleClients} width={242} height={96} />
                </CardImage>
            </CardImageLayout>
        </Card>
    );
}
